<template>
  <div
    class="asc-power-bi-dashboard-wrapper"
    data-automation-id="wrapper_power_bi_dashboard"
  >
    <div class="asc-power-bi-dashboard-wrapper--title">
      <div class="asc-power-bi-dashboard-wrapper--title-input">
        <asc-text-field-input
          v-model="dashboardName"
          :readonly="isReadonlyMode"
          :counter="true"
          :max-length="50"
          :hide-details="isReadonlyMode"
          :counter-exceed-message="getTextExceededMessage"
          :error-messages="dashboardNameErrorMessage"
          @change="debounceDashboardName"
        />
      </div>
      <template v-if="isReadonlyMode">
        <div class="asc-power-bi-dashboard-wrapper--title-column-menu">
          <asc-menu-btn
            :icon-only="ellipsisIcon"
            :items="getEllipsisItems"
            @onMenuSelect="handleEllipsesSelection"
          />
        </div>
      </template>
      <template v-if="!isReadonlyMode">
        <div
          class="asc-power-bi-dashboard-wrapper--title-column-buttons-cancel"
        >
          <asc-accent-button
            secondary
            :on-click="handleCancel"
            button-text="Cancel"
          ></asc-accent-button>
        </div>
        <div class="asc-power-bi-dashboard-wrapper--title-column-buttons-save">
          <asc-accent-button
            :on-click="handleSave"
            button-text="Save"
            :disabled="getSaveDisabled"
          ></asc-accent-button>
        </div>
      </template>
    </div>
    <div class="asc-power-bi-dashboard-wrapper--details">
      <div
        v-if="airlineName"
        class="asc-power-bi-dashboard-wrapper--details-column"
      >
        <asc-text-area-input
          label="Airline"
          :value="airlineName"
          :readonly="true"
          hide-details
        />
      </div>
      <div class="asc-power-bi-dashboard-wrapper--details-column">
        <asc-text-area-input
          label="Last Edited"
          :value="lastEdited"
          :readonly="true"
          hide-details
        />
      </div>
      <div class="asc-power-bi-dashboard-wrapper--details-column">
        <asc-text-area-input
          label="Dataset"
          :value="datasetName"
          :readonly="true"
          hide-details
        />
      </div>
      <div class="asc-power-bi-dashboard-wrapper--details-column">
        <asc-text-area-input
          label="Creator"
          :value="creator"
          :readonly="true"
          hide-details
        />
      </div>
    </div>
    <template v-if="loading">
      <div class="asc-power-bi-dashboard-wrapper--loading">
        <asc-spinner-wrapper
          loading
          class="asc-power-bi-dashboard-wrapper--loading-spinner"
        ></asc-spinner-wrapper>
      </div>
    </template>
    <div class="asc-power-bi-dashboard-wrapper--embed">
      <div id="embedContainerId" style="height: 750px" />
    </div>
  </div>
</template>

<script>
import AscSpinnerWrapper from '../atoms/AscSpinnerWrapper.vue'
import AscTextFieldInput from '../atoms/AscTextFieldInput.vue'
import AscAccentButton from '../atoms/AscAccentButton.vue'
import * as pbi from 'powerbi-client'
import AscMenuBtn from '../atoms/AscMenuBtn.vue'
import { getAscFormattedDateTime } from '@/utilities/dates'
import { debounceShortTime } from '@/utilities/appConsts'
import _ from 'lodash'
import AscTextAreaInput from '@/components/atoms/AscTextareaInput'
export default {
  name: 'AscPowerBiDashboardWrapper',

  components: {
    AscSpinnerWrapper,
    AscTextFieldInput,
    AscMenuBtn,
    AscAccentButton,
    AscTextAreaInput,
  },

  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    selectedDashboard: {
      type: Object,
      default: null,
    },
    updateSelectedDashboard: {
      type: Function,
      default: () => {},
    },
    dashboardEmbedToken: {
      type: Object,
      default: null,
    },
    selectedWorkspace: {
      type: Object,
      default: null,
    },
    airlineReports: {
      type: Array,
      default: () => [],
    },
    isReadonlyMode: {
      type: Boolean,
      default: true,
    },
    dashboardNameErrorMessage: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      report: null,
      powerBiService: null,
      embedContainer: null,
      setIntervalId: null,
      isEmbedded: false,
    }
  },

  computed: {
    dashboardName: {
      get() {
        return this.selectedDashboard?.name
      },
      set(value) {
        this.handleUpdateDashboardName(value)
      },
    },
    sharedWith() {
      return this.selectedDashboard?.isShared
        ? `All ${this.selectedDashboard?.pbiDatasets[0].pbiWorkspaceName} Users`
        : 'Only Me'
    },
    airlineName() {
      return this.selectedWorkspace?.friendlyName
    },
    lastEdited() {
      return getAscFormattedDateTime(this.selectedDashboard?.lastEdited)
    },
    datasetName() {
      return this.selectedDashboard?.pbiDatasets[0].name
    },
    creator() {
      return this.selectedDashboard?.isCollinsProvided
        ? 'Collins Ascentia'
        : this.selectedDashboard?.creator
    },
    ellipsisIcon() {
      return 'mdi-dots-vertical'
    },
    getEllipsisItems() {
      return this.selectedDashboard?.ellipseOptions.filter(
        o => o.title !== 'View',
      )
    },
    getTextExceededMessage() {
      return 'Text exceeding the allowed length'
    },
    getSaveDisabled() {
      return (
        this.dashboardNameErrorMessage !== '' ||
        this.selectedDashboard?.name.length > 50
      )
    },
  },

  watch: {
    dashboardEmbedToken(newToken) {
      if (newToken && this.isEmbedded) {
        this.updateToken()
      } else if (newToken && this.dashboardEmbedToken && !this.isEmbedded) {
        this.embedPowerBi()
      }
    },
    isReadonlyMode(newMode) {
      if (newMode === false && this.getSaveDisabled) {
        this.toggleMode(true)
      } else {
        this.toggleMode(newMode)
      }
    },
    dashboardNameErrorMessage(newValue) {
      if (!this.isReadonlyMode) {
        if (newValue !== '' || this.selectedDashboard?.name.length > 50) {
          this.toggleMode(true)
        } else {
          this.toggleMode(false)
        }
      }
    },
    selectedDashboard(value) {
      if (!this.isReadonlyMode) {
        if (value?.name.length > 50 || this.dashboardNameErrorMessage !== '') {
          this.toggleMode(true)
        } else {
          this.toggleMode(false)
        }
      }
    },
  },

  mounted() {
    this.powerBiService = new pbi.service.Service(
      pbi.factories.hpmFactory,
      pbi.factories.wpmpFactory,
      pbi.factories.routerFactory,
    )
    this.embedContainer = document.getElementById('embedContainerId')
    if (this.dashboardEmbedToken) {
      this.embedPowerBi()
    }
  },

  beforeDestroy() {
    this.isEmbedded = false
  },

  methods: {
    async updateToken() {
      try {
        // Get a reference to the embedded report.
        const report = this.powerBiService?.get(this.embedContainer)

        // Set the new access token
        await report.setAccessToken(this.dashboardEmbedToken?.embedToken)
      } catch (error) {
        console.log(`Update can't happen until after embed`, error)
      }
    },

    embedPowerBi() {
      const permissions = this.dashboardEmbedToken.isOwner
        ? pbi.models.Permissions.ReadWrite
        : pbi.models.Permissions.Read

      const viewMode = pbi.models.ViewMode.View

      const config = {
        type: 'report',
        tokenType: pbi.models.TokenType.Embed,
        accessToken: this.dashboardEmbedToken.embedToken,
        embedUrl: this.dashboardEmbedToken.url,
        id: this.dashboardEmbedToken.reportGuid,
        pageView: 'fitToWidth',
        permissions: permissions,
        viewMode: viewMode,
      }

      this.report = this.powerBiService?.embed(this.embedContainer, config)

      this.report.on('error', function (event) {
        console.log('Dashboards Error: %o', event)
      })

      this.report.on('saved', event => {
        // not calling this.handleSave() here do to the fact that
        // this.report.save() will also call this handler,
        // resulting in multiple API PUT requests
        this.$emit('handleSave')
        console.log('Dashboards Embed Save: %o', event)
      })

      this.report.on('loaded', event => {
        if (!this.getSaveDisabled) {
          this.toggleMode(this.isReadonlyMode)
        }
        console.log('Dashboards Embed Loaded: %o', event)
      })

      this.isEmbedded = true
    },
    async toggleMode(isReadonlyMode) {
      const modeName = isReadonlyMode ? 'view' : 'edit'
      this.report?.switchMode(modeName)
    },
    async reloadReport() {
      await this.report?.reload()
    },
    handleEllipsesSelection(selectedItem) {
      const callbackProperties = {
        selectionKey: selectedItem,
        selectedObject: this.selectedDashboard,
      }
      this.$emit('handleEllipsesSelection', callbackProperties)
    },
    async handleCancel() {
      this.$emit('handleCancel')
      await this.reloadReport()
      await this.toggleMode(true)
    },
    async handleSave() {
      this.$emit('handleSave')
      this.report.save()
    },
    checkValidName(value) {
      const callbackProperties = {
        dashboardName: value,
        workspaceId: this.selectedDashboard?.pbiDatasets[0].pbiWorkspaceId,
        isCloneReport: false,
        reportId: this.selectedDashboard?.id,
      }
      this.$emit('checkValidName', callbackProperties)
    },
    handleUpdateDashboardName(value) {
      this.$emit('isDirty')
      this.$emit('handleUpdateDashboardName', value)
    },
    debounceDashboardName: _.debounce(function (value) {
      this.checkValidName(value)
    }, debounceShortTime),
  },
}
</script>

<style lang="scss">
.asc-power-bi-dashboard-wrapper {
  &--title {
    display: flex;
    max-width: 100%;
  }

  &--title-input {
    flex: auto;
    padding: 0 16px 16px 0;
    max-width: 75%;

    .v-input {
      font-size: 25px;
      line-height: 28px;
      font-weight: 300;
      letter-spacing: 1.49px;
      color: $primary-black;
    }
  }

  &--title-column-menu {
    flex: initial;
    margin: auto 0 auto auto;
    &:hover,
    &:focus {
      background-color: $accent-100;
    }
    border-style: solid;
    border-width: 1px;
    border-color: $gray-500;
    border-radius: 2px;
  }

  &--title-column-buttons {
    flex: initial;

    &-cancel {
      margin-left: auto;
      margin-right: 8px;
    }

    .asc-accent-button-container {
      margin-top: 0;
      margin-right: 8px;
    }
  }

  &--loading {
    height: 750px;

    &-spinner {
      padding-top: 225;
    }
  }
}

.asc-power-bi-dashboard-wrapper--details {
  display: flex;
  max-width: 100%;
  padding: 0 0 16px 0;
  text-transform: none;

  &:first-child {
    flex: 2;
  }

  &-column {
    flex: 3;
  }
}
</style>
